import type { SubscriptionPlan } from './types';

export type PlanTier = 'starter' | 'growth' | 'pro';

export interface ComputedPlan {
  label: string;
  slug: string;
  priceMonthly: number;
}

const MODULE_PLAN_BASE: Record<string, string> = {
  inventory: 'h3-gear',
  safety: 'h3-safe',
  crew: 'h3-crew',
  projects: 'h3-project',
  payroll: 'h3-pay',
  work: 'h3-work',
  // H3Comply standalone plans are archived in Stripe and not sold separately;
  // it is only available as part of the H3 Complete bundle.
};

// Display names for modules that have no standalone plan (e.g. archived H3Comply)
const MODULE_DISPLAY_NAME: Record<string, string> = {
  compliance: 'H3Comply',
};

const THREE_MODULE_BUNDLE_BASE = 'h3-operations';
const COMPLETE_BUNDLE_BASE = 'h3-complete';

const TIER_ORDER: PlanTier[] = ['starter', 'growth', 'pro'];

/**
 * Determine the tier for a given team size using the plan catalog's
 * `max_users` limits. The canonical H3 Complete bundle is used as the
 * source of truth for tier thresholds.
 *
 * Returns `null` when the complete bundle plans are not present, so callers
 * can avoid showing a zero-price plan when the catalog is empty or incomplete.
 */
export function tierForTeamSize(teamSize: number, plans: SubscriptionPlan[]): PlanTier | null {
  const completePlans = TIER_ORDER.map((tier) =>
    plans.find((p) => p.slug === `${COMPLETE_BUNDLE_BASE}-${tier}`),
  );

  if (completePlans.some((plan) => !plan)) {
    return null;
  }

  for (let i = 0; i < TIER_ORDER.length; i++) {
    const plan = completePlans[i];
    const limit = plan?.max_users ?? Infinity;
    if (teamSize <= limit) {
      return TIER_ORDER[i];
    }
  }

  return 'pro';
}

/**
 * Compute the plan to display based on selected modules, available plans,
 * and team size. Returns null if 0 modules selected (plan step should be skipped).
 */
export function computePlan(
  selectedModules: string[],
  plans: SubscriptionPlan[],
  teamSize: number,
): ComputedPlan | null {
  const count = selectedModules.length;
  if (count === 0) return null;

  const tier = tierForTeamSize(teamSize, plans);
  if (!tier) return null;

  const findPlan = (slug: string) => plans.find((p) => p.slug === slug);

  if (count === 3) {
    const bundle = findPlan(`${THREE_MODULE_BUNDLE_BASE}-${tier}`);
    if (bundle) {
      return {
        label: '3-Module Bundle',
        slug: bundle.slug,
        priceMonthly: Number(bundle.price_monthly),
      };
    }
  }

  if (count === 7) {
    const bundle = findPlan(`${COMPLETE_BUNDLE_BASE}-${tier}`);
    if (bundle) {
      return {
        label: 'H3 Complete Bundle (all modules)',
        slug: bundle.slug,
        priceMonthly: Number(bundle.price_monthly),
      };
    }
  }

  // 1, 2, 4-5 modules: sum individual monthly prices for modules that have
  // a standalone plan. Modules without a base (e.g. archived H3Comply) are
  // skipped from pricing so no archived price slug can be submitted, but they
  // are still shown in the label as included-at-no-charge.
  let total = 0;
  const names: string[] = [];
  const includedNames: string[] = [];
  let primaryBase = '';
  for (const modKey of selectedModules) {
    const baseSlug = MODULE_PLAN_BASE[modKey];
    if (!baseSlug) {
      const displayName = MODULE_DISPLAY_NAME[modKey];
      if (displayName) {
        includedNames.push(`${displayName} (included)`);
      }
      continue;
    }
    if (!primaryBase) primaryBase = baseSlug;
    const slug = `${baseSlug}-${tier}`;
    const plan = findPlan(slug);
    if (plan) {
      total += Number(plan.price_monthly);
      names.push(plan.name.replace(/ (Starter|Growth|Pro)$/, ''));
    }
  }

  if (!primaryBase) {
    return null;
  }

  const allNames = [...names, ...includedNames];

  return {
    label: count === 1 ? (allNames[0] ?? 'Selected Module') : allNames.join(' + '),
    slug: `${primaryBase}-${tier}`,
    priceMonthly: total,
  };
}
